<template>
  <div>
    <slot></slot>
    <div v-if="!hasSlot" class="field-wrapper">
      <p v-if="label" class="label">{{ label }}</p>
      <textarea
        :rows="rows"
        :id="name"
        :name="name"
        :value="value"
        :placeholder="placeholder"
        @focus="onFocus"
        @input="onInput"
      />
    </div>
    <!-- デフォルトスロット利用時でも example を渡せるよう、footer は共通で描画する -->
    <div v-if="showFieldFooter" class="field-footer">
      <div class="example-slot">
        <slot name="example" />
      </div>
      <span v-if="!hasSlot && maxlength > 0" class="counter" :class="{ over: isOverLimit }">
        {{ formattedCurrentLength }}/{{ formattedMaxlength }}
      </span>
    </div>
    <p v-if="!hasSlot && showError" class="max-length-error">{{ errorMessage }}</p>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

export default Vue.extend({
  components: {},
  props: {
    value: {
      type: String,
    },
    maxlength: {
      type: Number,
      default: 0,
    },
    label: String,
    name: {
      type: String,
      default: "",
    },
    rows: {
      type: Number,
      default: 5,
    },
    placeholder: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      // フォーカス/入力後のみ文字数超過をエラーとして扱う（初期表示時の超過は許容）
      touched: false,
    };
  },
  computed: {
    hasSlot() {
      return !!this.$slots.default;
    },
    hasExampleSlot() {
      return !!(this.$slots.example || this.$scopedSlots.example);
    },
    showFieldFooter() {
      // textarea モード: example または文字数カウンターがあるとき
      // カスタム入力モード: example があるとき（デフォルトスロット利用時も example を描画可能）
      if (this.hasSlot) return this.hasExampleSlot;
      return this.hasExampleSlot || this.maxlength > 0;
    },
    currentLength(): number {
      return this.value?.length ?? 0;
    },
    formattedCurrentLength(): string {
      return this.currentLength.toLocaleString();
    },
    formattedMaxlength(): string {
      return this.maxlength.toLocaleString();
    },
    isOverLimit(): boolean {
      return this.maxlength > 0 && this.currentLength > this.maxlength;
    },
    showError(): boolean {
      return this.isOverLimit && this.touched;
    },
    errorMessage(): string {
      return this.$t("themePage.maxLengthError", { max: this.formattedMaxlength }) as string;
    },
  },
  watch: {
    // 保存可否は isOverLimit で判定（未タッチでも超過なら保存不可）
    // エラー文言の表示だけ showError（= touched）で制御する
    isOverLimit: {
      immediate: true,
      handler(over: boolean) {
        this.$emit("validation-change", over);
      },
    },
  },
  beforeDestroy() {
    // 親の fieldLengthErrors に超過フラグが残らないようクリアする
    // （現状は全項目常時描画だが、将来の出し分けに備える）
    this.$emit("validation-change", false);
  },
  methods: {
    onFocus() {
      if (this.isOverLimit) {
        this.touched = true;
      }
    },
    onInput(event: Event) {
      this.touched = true;
      this.$emit("input", (event.target as HTMLInputElement).value);
    },
  },
});
</script>

<style lang="scss" scoped>
.label {
  font-size: var(--font-size-xs);
  margin: 0 0 4px;
}

.field-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
  font-size: var(--font-size-xs);
}

.example-slot {
  min-width: 0;
}

.counter {
  margin-left: auto;
  flex-shrink: 0;
  color: var(--color-text-low);
}

.counter.over {
  color: var(--color-warning);
}

.max-length-error {
  margin: 4px 0 0;
  font-size: var(--font-size-xs);
  color: var(--color-error);
}

textarea {
  width: 100%;
  font-size: var(--font-size-sm);
  border: none;
  padding: 12px;
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  box-sizing: border-box;
  // 縦方向のみ可変。右下のハンドルをドラッグして高さを変更できる
  resize: vertical;
  min-height: 40px;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &::placeholder {
    color: var(--color-text-low);
  }
}
</style>
